/**
 * Japanese message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: polite です/ます (locked in l10n/locales.md).
 *
 * Platform terms follow Japanese UI conventions (設定 for settings,
 * テーマ for theme, 会話 for conversation, ペア設定 for pairing,
 * 削除 for dismiss, ミラーリング for mirror). "Glance" is the
 * untranslated feature brand name (glossary D1); "クリップ" renders the
 * D2 clip loanword in katakana.
 *
 * Plural notes: Japanese is an `other`-only CLDR locale — nouns take no
 * grammatical plural, so every `selectPlural("ja", …)` call supplies only
 * an `other` form (the `one` key is unused and omitted).
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "接続済み",
    statusReconnecting: "再接続中…",
    statusDisconnected: "切断されました",
    openSettings: "設定を開く",
    pairedFallbackDevice: "お使いのスマートフォン",
    publicModeToggleEnable: "通知の内容を隠す",
    publicModeToggleDisable: "通知の内容を表示",
  },

  notifications: {
    replyAriaLabel: "返信",
    replyPlaceholder: "返信…",
    replySend: "送信",
    sent: "送信しました",
    failedToSend: "送信に失敗しました",
    unread: "未読",
    dismiss: "削除",
    notificationImageAlt: "通知の画像",
  },

  conversations: {
    replyAriaLabel: "返信",
    replyPlaceholder: "返信…",
    replySend: "送信",
    sent: "送信しました",
    failedToSend: "送信に失敗しました",
    dismiss: "会話を削除",
    messagesListLabel: "この会話のメッセージ",
    deviceOwner: "自分",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "未読メッセージはありません"
        : `未読 ${count} ${selectPlural("ja", count, { other: "件" })}`,
  },

  list: {
    today: "今日",
    yesterday: "昨日",
    searchNotifications: "通知を検索",
    clearSearch: "検索をクリア",
    clearAll: "すべて削除",
    markAllRead: "すべて既読にする",
    groupCheckboxLabel: "グループ化",
    noNotificationsTitle: "通知はまだありません",
    noNotificationsDesc:
      "スマートフォンからミラーリングされた新しいメッセージがここに表示されます。",
    noMatchesTitle: "一致するものがありません",
    notificationsList: "通知",
    privacyAppList: "アプリ別の通知",
    clear: "削除",
    clearMatches: (count: number): string =>
      `${count} ${selectPlural("ja", count, { other: "件" })}の一致を削除`,
    noMatchesDesc: (query: string): string => `「${query}」に一致する通知はありません。`,
    appNotificationsGroup: (appName: string): string => `${appName} の通知`,
    dismissAllFrom: (appName: string): string => `${appName} の通知をすべて削除`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("ja", count, { other: "件のメッセージ" })
          : kind === "calls"
            ? selectPlural("ja", count, { other: "件の着信" })
            : selectPlural("ja", count, { other: "件の通知" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "削除",
    kbdNavigate: "移動",
    kbdOrSeparator: "または",
  },

  settings: {
    back: "通知に戻る",
    settings: "設定",
    appearance: "外観",
    theme: "テーマ",
    themeAuto: "自動",
    themeLight: "ライト",
    themeDark: "ダーク",
    language: "言語",
    languageSystemDefault: "システムのデフォルト",
    notificationsSection: "通知",
    osNotifications: "OS 通知",
    callNotifications: "着信通知",
    privacySection: "プライバシー",
    analyticsToggle: "匿名の利用統計",
    analyticsToggleDescription: "件数とマイルストーンのみ — 通知の内容は送信しません。",
    connection: "接続",
    status: "ステータス",
    statusConnected: "接続済み",
    statusReconnecting: "再接続中…",
    statusDisconnected: "切断されました",
    disconnectFromPhone: "スマートフォンから切断",
    disconnectConfirm: "よろしいですか？ デバイスのペア設定が解除されます。",
    yesDisconnect: "はい、切断します",
    cancel: "キャンセル",
    aboutAndSupport: "情報とサポート",
    version: "バージョン",
    encryption: "暗号化",
    encryptionValue: "E2E (NaCl)",
    needHelp: "お困りですか？ __EMAIL__ にメールしてください",
    disconnectFailed: "切断に失敗しました — もう一度お試しください",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `切断しましたが、ローカルのクリーンアップに失敗しました: ${errorMsg}。ページを再読み込みしてください。`,
    disconnectLocalCleanupFailed:
      "切断しましたが、ローカルのクリーンアップに失敗しました。ページを再読み込みしてください。",
  },

  pairing: {
    enterCodeInstruction: "このコードを Reflecto アプリに入力してください",
    pairedSuccessfully: "ペア設定が完了しました！",
    pairingFailed: "ペア設定に失敗しました",
    requestingPairingCode: "ペア設定コードを要求しています…",
    codeExpired: "コードの有効期限が切れました",
    tryAgain: "もう一度試す",
    failedToStartPairing: "ペア設定を開始できませんでした",
    needAppText: "Android アプリが必要ですか？ __EMAIL__ にメールしてアクセスを取得してください。",
  },

  dnd: {
    title: "サイレント",
    description: "このデバイスの通知を1時間停止します。",
    snooze1h: "1時間スヌーズ",
    snoozeActiveUntil: (formattedTime: string): string => `${formattedTime} まで有効`,
    cancelSnooze: "キャンセル",
  },

  connectors: {
    title: "連携済みサービス",
    emptyBody:
      "連携済みのサービスはまだありません。ChatGPT や Claude をそれぞれのコネクター設定から連携し、スマートフォンでコードを承認してください。",
    scopeSend: "通知を送信できます",
    added: (date: string): string => `追加 ${date}`,
    lastUsed: (relative: string): string => `最終使用 ${relative}`,
    neverUsed: "未使用",
    revoke: "切断",
    revokeConfirm: (label: string): string =>
      `${label} を切断しますか？ 直ちにアクセスを失います。`,
    revokeAction: "切断",
    revokeConfirmLabel: (label: string): string => `${label} を切断`,
    cancel: "キャンセル",
    loadError: "連携済みサービスを読み込めませんでした。もう一度お試しください。",
    revokeError: "サービスを切断できませんでした。もう一度お試しください。",
    retry: "再試行",
  },

  feedback: {
    title: "フィードバックを送信",
    close: "閉じる",
    messageLabel: "メッセージ",
    messagePlaceholder: "バグを見つけた、アイデアがある、何かおかしいと感じた？ お聞かせください。",
    categoryLabel: "カテゴリ",
    categoryBug: "バグ",
    categoryIdea: "アイデア",
    categoryOther: "その他",
    emailLabel: "メールアドレス（任意・返信が必要な場合のみ）",
    emailPlaceholder: "you@example.com",
    emailHelp: "メーリングリストに追加することはありません。",
    disclosure:
      "お送りいただいたメッセージは Reflecto のサーバーに送信され、社内の課題トラッカーに記録されます。レポートを照合するため、アプリのバージョンと、ペア設定時に割り当てられたデバイス ID を添付します。通知・連絡先・その他の個人データが私たちに見えることはありません。メールアドレスは任意です。返信をご希望の場合のみご記入ください。メーリングリストに追加することはありません。",
    submit: "フィードバックを送信",
    sending: "送信中…",
    success: "ありがとうございます — 通常24時間以内に人間が読みます",
    error: "送信できませんでした。もう一度お試しください。",
    done: "完了",
    sendFeedback: "フィードバックを送信",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "閉じる",
    aiSummaryTag: "AI 要約",
    errorAi: "現在、要約を生成できませんでした。",
    retryCta: "もう一度試す",
    downloadHeading: "AI 要約を利用する",
    downloadBody:
      "オンデバイスの AI 要約で、見逃した内容に追いつきましょう。すべてお使いのデバイス上で動作します。",
    downloadCta: "モデルをダウンロード",
    downloadPrivacyNote: "ダウンロードは一度だけです。サーバーが通知を見ることはありません。",
    downloadingHeading: "モデルをダウンロード中…",
    caughtUp: "すべて確認済みです — 今、対応が必要なものはありません。",
    needsYou: "要対応",
    quiet: "その他",
    scanning: (pending: number): string => `${pending} 件を要約中…`,
    jump: "通知を開く",
    affordanceReply: "返信",
    affordanceOpen: "開く",
    affordanceCopyOtp: "コードをコピー",
    buttonLabel: "Glance",
  },

  update: {
    title: "アップデートがあります",
    description: "Reflecto の新しいバージョンが利用可能です。再読み込みして更新してください。",
    reload: "再読み込み",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `このデバイスを「${name}」と名付けました。`,
    cardLooksGood: "OK",
    cardEdit: "編集",
    cardAriaLabel: "デバイス名を確認",
    renameTitle: "このデバイスの名前を変更",
    renameLabel: "デバイス名",
    renameSave: "保存",
    renameCancel: "キャンセル",
    renameFailed: "名前を変更できませんでした。もう一度お試しください。",
  },

  collision: {
    title: (name: string): string => `「${name}」はすでに存在します`,
    subtitle: "このデバイスを何と呼びますか？",
    chipWork: (name: string): string => `仕事用 ${name}`,
    chipHome: (name: string): string => `自宅用 ${name}`,
    chipPersonal: (name: string): string => `個人用 ${name}`,
    customLabel: "カスタム名",
    save: "保存",
    useAnyway: "元の名前をそのまま使う",
    saveFailed: "新しい名前を保存できませんでした。もう一度お試しください。",
    ariaLabel: "デバイス名の重複を解決",
  },

  clipboard: {
    title: "クリップボード",
    empty: "他のデバイスからのクリップがここに表示されます。タップしてコピー。",
    from: (label: string): string => `${label} から`,
    copy: "クリップボードにコピー",
    copied: "コピーしました",
    reveal: "表示",
    hide: "隠す",
    maskedHint: "内容は非表示です — 「表示」を押して表示",
    sensitiveChip: "機密",
    cloudChip: "API トークン経由",
    sendPlaceholder: "送信するテキストを貼り付け…",
    sendCta: "送信",
    sendSensitiveToggle: "機密としてマーク",
    sendSuccess: (n: number): string =>
      `${n} ${selectPlural("ja", n, { other: "台" })}のデバイスに送信しました。`,
    sendPartial: (n: number, total: number): string =>
      `${total} 台中 ${n} ${selectPlural("ja", total, { other: "台" })}のデバイスに送信しました。`,
    sendNoDevices: "送信先のペア設定済みデバイスがありません。",
    sendFailed: "送信できませんでした。もう一度お試しください。",
    sendEmpty: "送信するものがありません。",
    dockExpand: "クリップの履歴を表示",
    dockCollapse: "折りたたむ",
    dockSendTitle: "デバイスに送信",
    dockFromClipboard: (chars: number): string => `クリップボードから · ${chars} 文字`,
    dockSend: (n: number): string => `${n} 台に送信`,
    dockCancel: "キャンセル",
    dockRecentHeading: "最近",
    dockEmptyClipboard: "クリップボードは空です。",
    shareTitle: "デバイスに送信",
    shareReview:
      "送信する内容を確認してください。ペア設定済みのデバイスにエンドツーエンド暗号化で送信されます。",
    shareSend: "送信",
    shareCancel: "キャンセル",
    shareNotPaired: "共有テキストを送信するには、先にデバイスをペア設定してください。",
    shareOpenApp: "Reflecto を開く",
  },
  install: {
    title: "Reflecto をインストール",
    body: "ホーム画面に追加すると、すばやくアクセスできます。",
    cta: "インストール",
    dismissAria: "インストールの案内を閉じる",
  },
  common: {
    loading: "読み込み中",
  },
  errors: {
    notPaired: "ペア未設定",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${os} の ${browser}`,
    webFallback: "ウェブブラウザ",
  },
  palette: {
    placeholder: "コマンドを入力…",
    noResults: "一致するコマンドがありません",
    ariaLabel: "コマンドパレット",
    clearAll: "すべての通知を削除",
    glance: "Glanceの要約",
    cancelSnooze: "スヌーズを解除",
    copyClip: "受信した最新のクリップをコピー",
  },
} satisfies MessagesShape;
